import React, { useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext.js';
import { AuthProvider, useAuth } from './context/AuthContext.js';
import { NotificationProvider } from './context/NotificationContext.js';

import { Sidebar } from './components/layout/Sidebar.js';
import { Header } from './components/layout/Header.js';
import { UniversalSearchModal } from './components/search/UniversalSearchModal.js';
import { NotificationDrawer } from './components/notifications/NotificationDrawer.js';
import { Skeleton } from './components/ui/Skeleton.js';
import { BrandedLoader } from './components/ui/BrandedLoader.js';
import ErrorBoundary from './components/ui/ErrorBoundary.js';

import './theme/global.css';

// Lazy-loaded route components for high performance code-splitting
const LoginPage = lazy(() => import('./pages/auth/LoginPage.js').then((m) => ({ default: m.LoginPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage.js').then((m) => ({ default: m.NotFoundPage })));
const ForbiddenPage = lazy(() => import('./pages/ForbiddenPage.js').then((m) => ({ default: m.ForbiddenPage })));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage.js').then((m) => ({ default: m.DashboardPage })));
const UsersPage = lazy(() => import('./pages/users/UsersPage.js').then((m) => ({ default: m.UsersPage })));
const RolesPage = lazy(() => import('./pages/roles/RolesPage.js').then((m) => ({ default: m.RolesPage })));
const DepartmentsPage = lazy(() => import('./pages/departments/DepartmentsPage.js').then((m) => ({ default: m.DepartmentsPage })));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage.js').then((m) => ({ default: m.SettingsPage })));
const AuditLogsPage = lazy(() => import('./pages/audit/AuditLogsPage.js').then((m) => ({ default: m.AuditLogsPage })));

const FinanceDashboardPage = lazy(() => import('./pages/finance/FinanceDashboardPage.js').then((m) => ({ default: m.FinanceDashboardPage })));
const ChartOfAccountsPage = lazy(() => import('./pages/finance/ChartOfAccountsPage.js').then((m) => ({ default: m.ChartOfAccountsPage })));
const TransactionsPage = lazy(() => import('./pages/finance/TransactionsPage.js').then((m) => ({ default: m.TransactionsPage })));
const InvoicesPage = lazy(() => import('./pages/finance/InvoicesPage.js').then((m) => ({ default: m.InvoicesPage })));
const ExpensesPage = lazy(() => import('./pages/finance/ExpensesPage.js').then((m) => ({ default: m.ExpensesPage })));
const BudgetsPage = lazy(() => import('./pages/finance/BudgetsPage.js').then((m) => ({ default: m.BudgetsPage })));
const OwnerInvestmentsPage = lazy(() => import('./pages/finance/OwnerInvestmentsPage.js').then((m) => ({ default: m.OwnerInvestmentsPage })));
const FinancialReportsPage = lazy(() => import('./pages/finance/FinancialReportsPage.js').then((m) => ({ default: m.FinancialReportsPage })));

const HrDashboardPage = lazy(() => import('./pages/hrms/HrDashboardPage.js').then((m) => ({ default: m.HrDashboardPage })));
const AttendancePage = lazy(() => import('./pages/hrms/AttendancePage.js').then((m) => ({ default: m.AttendancePage })));
const LeaveManagementPage = lazy(() => import('./pages/hrms/LeaveManagementPage.js').then((m) => ({ default: m.LeaveManagementPage })));
const DailyReportsPage = lazy(() => import('./pages/hrms/DailyReportsPage.js').then((m) => ({ default: m.DailyReportsPage })));
const TaskManagementPage = lazy(() => import('./pages/hrms/TaskManagementPage.js').then((m) => ({ default: m.TaskManagementPage })));
const PerformancePage = lazy(() => import('./pages/hrms/PerformancePage.js').then((m) => ({ default: m.PerformancePage })));
const AnnouncementsPage = lazy(() => import('./pages/hrms/AnnouncementsPage.js').then((m) => ({ default: m.AnnouncementsPage })));

const CrmDashboardPage = lazy(() => import('./pages/crm/CrmDashboardPage.js').then((m) => ({ default: m.CrmDashboardPage })));
const LeadsPage = lazy(() => import('./pages/crm/LeadsPage.js').then((m) => ({ default: m.LeadsPage })));
const ClientsPage = lazy(() => import('./pages/crm/ClientsPage.js').then((m) => ({ default: m.ClientsPage })));
const QuotationsPage = lazy(() => import('./pages/crm/QuotationsPage.js').then((m) => ({ default: m.QuotationsPage })));
const MeetingsPage = lazy(() => import('./pages/crm/MeetingsPage.js').then((m) => ({ default: m.MeetingsPage })));
const ServicesPage = lazy(() => import('./pages/crm/ServicesPage.js').then((m) => ({ default: m.ServicesPage })));

const ProjectsDashboardPage = lazy(() => import('./pages/projects/ProjectsDashboardPage.js').then((m) => ({ default: m.ProjectsDashboardPage })));
const ProjectsListPage = lazy(() => import('./pages/projects/ProjectsListPage.js').then((m) => ({ default: m.ProjectsListPage })));
const ProjectDetailPage = lazy(() => import('./pages/projects/ProjectDetailPage.js').then((m) => ({ default: m.ProjectDetailPage })));
const DeliverablesPage = lazy(() => import('./pages/projects/DeliverablesPage.js').then((m) => ({ default: m.DeliverablesPage })));
const ResourceBookingPage = lazy(() => import('./pages/projects/ResourceBookingPage.js').then((m) => ({ default: m.ResourceBookingPage })));

const PageLoaderFallback: React.FC = () => (
  <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
    <Skeleton height="32px" width="300px" />
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-4)' }}>
      <Skeleton height="100px" />
      <Skeleton height="100px" />
      <Skeleton height="100px" />
      <Skeleton height="100px" />
    </div>
    <Skeleton height="300px" />
  </div>
);

interface ProtectedRouteProps {
  children: React.ReactNode;
  module?: string;
  action?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, module, action }) => {
  const { user, loading, hasPermission } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  if (loading) {
    return <BrandedLoader message="Loading Drishti Business OS..." fullScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (module && action && !hasPermission(module, action)) {
    return <Navigate to="/403" replace />;
  }

  return (
    <NotificationProvider>
      <ErrorBoundary>
        <div className="app-layout">
          <Sidebar collapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)} />
          <div className="main-wrapper">
            <Header onOpenSearch={() => setIsSearchOpen(true)} />
            <main className="main-content">
              <Suspense fallback={<PageLoaderFallback />}>{children}</Suspense>
            </main>
          </div>

          {/* Global Search & Notifications */}
          <UniversalSearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
          <NotificationDrawer />
        </div>
      </ErrorBoundary>
    </NotificationProvider>
  );
};

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={
                <Suspense fallback={<PageLoaderFallback />}>
                  <LoginPage />
                </Suspense>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute module="USERS" action="VIEW">
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roles"
              element={
                <ProtectedRoute module="ROLES" action="VIEW">
                  <RolesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/departments"
              element={
                <ProtectedRoute module="DEPARTMENTS" action="VIEW">
                  <DepartmentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute module="SETTINGS" action="VIEW">
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit"
              element={
                <ProtectedRoute module="AUDIT" action="VIEW">
                  <AuditLogsPage />
                </ProtectedRoute>
              }
            />

            {/* PHASE 2: FINANCE ROUTES */}
            <Route
              path="/finance/dashboard"
              element={
                <ProtectedRoute module="FINANCE" action="VIEW">
                  <FinanceDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/coa"
              element={
                <ProtectedRoute module="FINANCE" action="VIEW">
                  <ChartOfAccountsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/transactions"
              element={
                <ProtectedRoute module="FINANCE" action="VIEW">
                  <TransactionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/invoices"
              element={
                <ProtectedRoute module="FINANCE" action="VIEW">
                  <InvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/expenses"
              element={
                <ProtectedRoute module="FINANCE" action="VIEW">
                  <ExpensesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/budgets"
              element={
                <ProtectedRoute module="FINANCE" action="VIEW">
                  <BudgetsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/investments"
              element={
                <ProtectedRoute module="FINANCE" action="VIEW">
                  <OwnerInvestmentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finance/reports"
              element={
                <ProtectedRoute module="FINANCE" action="VIEW">
                  <FinancialReportsPage />
                </ProtectedRoute>
              }
            />

            {/* PHASE 3: HRMS & TEAM MANAGEMENT ROUTES */}
            <Route
              path="/hrms/dashboard"
              element={
                <ProtectedRoute module="HRMS" action="VIEW">
                  <HrDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hrms/attendance"
              element={
                <ProtectedRoute>
                  <AttendancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hrms/leave"
              element={
                <ProtectedRoute>
                  <LeaveManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hrms/daily-reports"
              element={
                <ProtectedRoute>
                  <DailyReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hrms/tasks"
              element={
                <ProtectedRoute>
                  <TaskManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hrms/performance"
              element={
                <ProtectedRoute module="HRMS" action="VIEW">
                  <PerformancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hrms/announcements"
              element={
                <ProtectedRoute>
                  <AnnouncementsPage />
                </ProtectedRoute>
              }
            />

            {/* PHASE 4: CRM & CLIENT MANAGEMENT ROUTES */}
            <Route
              path="/crm/dashboard"
              element={
                <ProtectedRoute module="CRM" action="VIEW">
                  <CrmDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/leads"
              element={
                <ProtectedRoute module="CRM" action="VIEW">
                  <LeadsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/clients"
              element={
                <ProtectedRoute module="CRM" action="VIEW">
                  <ClientsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/quotations"
              element={
                <ProtectedRoute module="CRM" action="VIEW">
                  <QuotationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/meetings"
              element={
                <ProtectedRoute module="CRM" action="VIEW">
                  <MeetingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crm/services"
              element={
                <ProtectedRoute module="CRM" action="VIEW">
                  <ServicesPage />
                </ProtectedRoute>
              }
            />

            {/* PHASE 5: PROJECT & OPERATIONS ROUTES */}
            <Route
              path="/projects/dashboard"
              element={
                <ProtectedRoute module="PROJECTS" action="VIEW">
                  <ProjectsDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/list"
              element={
                <ProtectedRoute module="PROJECTS" action="VIEW">
                  <ProjectsListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/detail/:id"
              element={
                <ProtectedRoute module="PROJECTS" action="VIEW">
                  <ProjectDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/deliverables"
              element={
                <ProtectedRoute module="PROJECTS" action="VIEW">
                  <DeliverablesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/resources"
              element={
                <ProtectedRoute module="PROJECTS" action="VIEW">
                  <ResourceBookingPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/403"
              element={
                <ProtectedRoute>
                  <ForbiddenPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/404"
              element={
                <ProtectedRoute>
                  <NotFoundPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
